import DatasetTable from "@/components/DatasetTable/DatasetTable";
import { Datasets } from "@/components/ImpactReferenceSelector/Datasets";
import ImpactReferenceModal from "@/components/ImpactReferenceSelector/ImpactReferenceModal";
import Skeleton from "@/components/Skeleton/Skeleton";
import useAnalytics from "@/hooks/useAnalytics";
import { ImpactDataset } from "@/hooks/useEcoInventDatasets";
import useIntercom from "@/hooks/useIntercom";
import useTranslation from "@/hooks/useTranslation";
import DeleteEolAction from "@/pages/products/eol/DeleteEolAction";
import styles from "@/pages/products/eol/EolImpactModal.module.scss";
import { ProductEol, useGetEol, usePostEndOfLife } from "@/pages/products/eol/useEol";
import { DeleteOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { Button, message, Space } from "antd";
import React, { useEffect } from "react";

function EolImpactModal({
  selectedProducts,
  onSuccess,
  children,
}: {
  selectedProducts: ProductEol[];
  onSuccess: () => void;
  children: React.ReactElement;
}) {
  const analytics = useAnalytics();
  const [selectedDataset, setSelectedDataset] = React.useState<ImpactDataset>();
  const [activeTab, setActiveTab] = React.useState<string>("suggestions");
  const [open, setOpen] = React.useState<boolean>(false);
  const translation = useTranslation();
  const postEndOfLife = usePostEndOfLife();
  const { setMarginBottom } = useIntercom();

  const { data, isFetching: dataIsFetching } = useGetEol({
    productId: selectedProducts[0].id,
    enabled: selectedProducts.length === 1 && open,
  });

  useEffect(() => {
    if (data?.match && selectedProducts?.length === 1) {
      setSelectedDataset(data.match);
    } else {
      setSelectedDataset(undefined);
    }
  }, [data, selectedProducts]);

  const onDatasetChange = (dataset?: any) => {
    setSelectedDataset(dataset);
  };

  const onFinish = () => {
    if (selectedDataset) {
      return postEndOfLife
        .mutateAsync({
          productIds: selectedProducts.map((product) => product.id),
          match_id: selectedDataset.id,
        })
        .then(() => {
          message.success(translation.eol.impactModal.successMessage);
          analytics.track("EOL reference edited");
          onSuccess();
        })
        .catch(() => {
          message.error(translation.errors.general);
        });
    }
  };

  const onOpen = () => {
    setOpen(true);
    setMarginBottom(85);
  };

  const onClose = () => {
    setOpen(false);
    setMarginBottom(0);
  };

  const CurrentSelectionContent = (
    <>
      <div className={styles["current-selection"]}>
        <span className={styles.title}>For product{selectedProducts.length === 1 ? "" : "s"}</span>
        <span className={styles.material}>
          {selectedProducts.length > 1 ? (
            `${selectedProducts.length} selected`
          ) : !data || dataIsFetching ? (
            <Skeleton style={{ width: 150, height: 24 }} />
          ) : (
            selectedProducts[0].name
          )}
        </span>
        <span
          className={styles.title}
          style={{ marginTop: 4 }}
        >
          Currently saved impact reference
        </span>
        {selectedProducts.length > 1 ? (
          <span className={styles.material}>
            Selecting and saving a reference will overwrite any saved references for the selected
            products.
          </span>
        ) : !data || dataIsFetching ? (
          <Skeleton style={{ width: 300, height: 40 }} />
        ) : data?.match ? (
          <DatasetTable
            type="grey"
            data={data?.match && [data.match]}
            emptyState={{ noResultsMessage: <span>No reference selected yet.</span> }}
            hideHeader={true}
            hideRaster={true}
            activeRowKey={selectedDataset?.id}
            onRowClick={(record) => setSelectedDataset(record)}
          />
        ) : (
          <span className={styles.material}>No reference saved yet.</span>
        )}
      </div>
      <div className={styles["info-message"]}>
        <Space>
          <InfoCircleOutlined />
          <span>{translation.eol.impactModal.infoMessage}</span>
        </Space>
      </div>
    </>
  );

  return (
    <>
      <span onClick={onOpen}>{children}</span>
      {open && (
        <ImpactReferenceModal
          title={translation.eol.impactModal.pageTitle}
          onClose={onClose}
          onSubmit={onFinish}
          currentSelectionContent={CurrentSelectionContent}
          isLoading={postEndOfLife.isLoading}
          selectedDataset={selectedDataset}
          onDatasetChange={onDatasetChange}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          defaultDatabaseSearch={{ sector: "Waste Treatment & Recycling" }}
          bottomLeft={
            data?.match &&
            selectedProducts.length === 1 && (
              <DeleteEolAction
                selectedProducts={selectedProducts}
                onSuccess={() => onClose()}
              >
                <Button
                  icon={<DeleteOutlined />}
                  danger
                />
              </DeleteEolAction>
            )
          }
          suggestionTabContent={
            <Datasets
              key="EOL"
              selectedDataset={selectedDataset}
              onDatasetChange={onDatasetChange}
              datasetType="eol"
            />
          }
        />
      )}
    </>
  );
}

export default EolImpactModal;
